import { faRefresh } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { useFormik } from "formik";
import { AlertState } from "global/AlertState";
import { loadingState } from "global/LoaderState";
import { LoginState } from "global/LoginState";
import { useEffect, useRef, useState } from "react";
import { FaEye, FaEyeSlash } from "react-icons/fa";
import { useNavigate } from "react-router-dom";
import { useRecoilState, useSetRecoilState } from "recoil";
import callApi from "services/api";
import { generateCaptcha } from "services/CaptchaService";
import { generateSHA512HashAndEncryptInES6 } from "services/encryption";
import * as Yup from "yup";

const LoginPage = () => {
  const [, setAlertMessage] = useRecoilState(AlertState);
  const setLoginState = useSetRecoilState(LoginState);

  const canvasRef = useRef(null);
  const [captcha, setCaptcha] = useState("");

  useEffect(() => {
    generateCaptcha(setCaptcha, canvasRef);
  }, []);

  const navigate = useNavigate();
  const [loading, setLoading] = useRecoilState(loadingState);
  const [showPassword, setShowPassword] = useState(false);
  const formik = useFormik({
    initialValues: {
      username: "",
      password: "",
      captchaInput: "",
    },
    validationSchema: Yup.object({
      username: Yup.string().required("Please enter your username."),
      password: Yup.string()
        .required("Please provide your password.")
        .matches(
          /[A-Z]/,
          "Password must contain at least one uppercase letter.",
        )
        .matches(
          /[a-z]/,
          "Password must contain at least one lowercase letter.",
        )
        .matches(/[0-9]/, "Password must contain at least one number.")
        .matches(
          /[!@#$%^&*(),.?":{}|<>]/,
          "Password must contain at least one special character.",
        ),
      captchaInput: Yup.string().required("Please enter captcha."),
    }),
    onSubmit: async (values) => {
      if (values.captchaInput !== captcha) {
        setAlertMessage("Invalid captcha. Please try again.");
        generateCaptcha(setCaptcha, canvasRef);
        return;
      }
      const encryptedData = {
        username: values.username,
        password: generateSHA512HashAndEncryptInES6(values?.password),
      };
      await login(encryptedData);
    },
  });

  const handleKeyDown = (e) => {
    if (e.key === "Enter") {
      formik.handleSubmit();
    }
  };

  const login = async (formdata) => {
    setLoading(true);
    var response = await callApi(
      "/authenticate",
      "POST",
      formdata,
      true,
      false,
      setAlertMessage,
      navigate,
      setLoginState,
    );
    if (response?.success) {
      setLoginState(true);
    }
    setLoading(false);
  };

  return (
    <>
      <div className="row justify-content-center">
        <div className="col-md-8">
          <div className="card mt-3">
            <div className="card-face card-front">
              <div className="card-body">
                <h3 className="card-title text-center">Login</h3>
                <h6 className="text-secondary">
                  Welcome ! Please enter your details.
                </h6>
                <form onSubmit={formik.handleSubmit} onKeyDown={handleKeyDown}>
                  <div className="mb-3">
                    <label htmlFor="username" className="form-label mt-3">
                      Username
                      <span className="text-danger">*</span>
                    </label>
                    <input
                      type="text"
                      id="username"
                      name="username"
                      className={`form-control ${
                        formik.touched.username && formik.errors.username
                          ? "is-invalid"
                          : ""
                      }`}
                      {...formik.getFieldProps("username")}
                      placeholder="Enter Username"
                      autoComplete="username"
                      maxLength={100}
                    />
                    {formik.touched.username && formik.errors.username && (
                      <div className="invalid-feedback">
                        {formik.errors.username}
                      </div>
                    )}
                  </div>
                  <div className="mb-3">
                    <label htmlFor="username" className="form-label">
                      Password:<span className="text-danger">*</span>
                    </label>
                    <div className="position-relative">
                      <input
                        type={showPassword ? "text" : "password"}
                        id="password"
                        name="password"
                        className={`form-control ${
                          formik.touched.password && formik.errors.password
                            ? "is-invalid"
                            : ""
                        }`}
                        {...formik.getFieldProps("password")}
                        placeholder="Enter Password"
                        maxLength={50}
                      />
                      <span
                        className="position-absolute top-50 end-0 translate-middle-y"
                        onClick={() => setShowPassword((prev) => !prev)}
                        style={{
                          cursor: "pointer",
                          marginRight: formik.errors.password
                            ? "1.99rem"
                            : "1rem",
                        }}
                      >
                        {showPassword ? <FaEyeSlash /> : <FaEye />}
                      </span>
                    </div>
                    {formik.touched.password && formik.errors.password && (
                      <div
                        style={{
                          color: "#f03d3d",
                          fontSize: "0.75rem",
                          marginTop: "0.375rem",
                        }}
                      >
                        {formik.errors.password}
                      </div>
                    )}
                  </div>

                  <div className="mb-3">
                    <label className="form-label">
                      Enter Captcha
                      <span className="text-danger">*</span>
                    </label>

                    <div className="d-flex align-items-center gap-2">
                      <canvas
                        ref={canvasRef}
                        style={{
                          border: "1px solid #ccc",
                          background: "#f5f5f5",
                        }}
                      />

                      <button
                        type="button"
                        className="btn btn-secondary"
                        onClick={() => generateCaptcha(setCaptcha, canvasRef)}
                      >
                        <FontAwesomeIcon icon={faRefresh} className="me-1" />{" "}
                        Refresh
                      </button>
                    </div>

                    <input
                      type="text"
                      name="captchaInput"
                      className={`form-control mt-2 ${
                        formik.touched.captchaInput &&
                        formik.errors.captchaInput
                          ? "is-invalid"
                          : ""
                      }`}
                      placeholder="Enter captcha"
                      autoComplete="off"
                      maxLength={6}
                      {...formik.getFieldProps("captchaInput")}
                    />

                    {formik.touched.captchaInput &&
                      formik.errors.captchaInput && (
                        <div className="invalid-feedback">
                          {formik.errors.captchaInput}
                        </div>
                      )}
                  </div>

                  {/* <div className="d-flex justify-content-center mb-3">
                    Forgot Password?{" "}
                    <b
                      className="text-info ms-1 cursor-pointer"
                      onClick={() => setLoginView("forgot-password")}
                    >
                      Click here
                    </b>
                  </div> */}

                  <div className="d-flex justify-content-center">
                    <button className="btn btn-info w-100" disabled={loading}>
                      Login
                    </button>
                  </div>
                </form>
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default LoginPage;
